import { useTheme } from '@emotion/react';
import type { CenterProps } from '@hover/blueprint';
import { Center, forwardRef, motion } from '@hover/blueprint';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import React from 'react';

import { exitStyles } from 'src/shared/helpers/motion';

import { isGeneratingImage } from '../../helpers';
import { useMaxWidthQuery } from '../../hooks';
import { getImageId, isGenerated, pickImage } from '../../types';

import { $carouselImageGap, CarouselImage } from './CarouselImage';
import { useFullscreenImageContents } from './context';
import {
  layoutBreakpoint,
  presenceVariantsWithDirection,
  useCarouselTransition,
  useIsFullscreenImageMobile,
} from './helpers';

export interface FullscreenImageCarouselProps extends CenterProps {
  activateZoom: () => void;
}

/**
 * Determine how to style and animate the adjacent next and previous images. On
 * mobile, the adjacent images are static and always visible so the user knows
 * to swipe.
 */
const getPresenceStyles = (
  direction: 'next' | 'previous',
  isMobile: boolean,
) => {
  const x = direction === 'next' ? '100%' : '-100%';

  return isMobile ? { x, opacity: 1 } : { x, opacity: 0 };
};

const MotionCenter = motion(Center);

const FullscreenImageCarousel = forwardRef<FullscreenImageCarouselProps, 'div'>(
  ({ activateZoom, sx, ...props }, ref) => {
    const {
      carouselMotion,
      direction,
      image,
      imagePair,
      nextImage,
      previousImage,
    } = useFullscreenImageContents();

    const { space } = useTheme();

    const gapMobile = 0;
    const gapDesktop = space[500];

    const isMobile = useIsFullscreenImageMobile();
    const belowLayoutBreakpoint = useMaxWidthQuery(layoutBreakpoint);

    const placeholderSrc = imagePair.toggleImage?.url;

    // When generating subsequent images, we need an image that's already loaded
    // to preserve the same image size for the generation loading placeholder
    const referenceImage = [previousImage, image, nextImage]
      .map(pickImage)
      .filter(isGenerated)
      .find(i => !isGeneratingImage(i));

    // clicking central image will activate zoom
    // if we are in a mobile device
    const handleZoomActivation = () => {
      if (!isMobile) return;
      activateZoom();
    };

    return (
      <Center
        aria-label="Image Carousel"
        boxSizing="border-box"
        height="100%"
        minHeight={0}
        width="100%"
        {...props}
        ref={ref}
        sx={{
          [$carouselImageGap.variable]: { base: gapMobile, tablet: gapDesktop },
          ...sx,
        }}
      >
        <MotionCenter
          height="100%"
          position="relative"
          style={{ x: carouselMotion }}
          sx={{
            ...exitStyles('.carousel-image', {
              top: 'unset',
            }),
            // On mobile, since we display the adjacent images as part of this
            // image component, hide the component that is exiting entirely to
            // prevent a weird overlaid misaligned state that happens when the
            // aspect ratio changes. This doesn't use the `exitStyles` helper,
            // as there alse seems to a bug in Framer Motion that causes the
            // exiting element to intermittently not get removed at all, so this
            // hides all but the latest element.
            ...belowLayoutBreakpoint({
              '& .carousel-image:not(.carousel-image:last-of-type)': {
                visibility: 'hidden',
              },
            }),
          }}
        >
          <MotionConfig transition={useCarouselTransition()}>
            <AnimatePresence
              custom={direction}
              initial={false}
              mode="popLayout"
            >
              <CarouselImage
                {...presenceVariantsWithDirection(direction, {
                  left: getPresenceStyles('previous', isMobile),
                  center: {
                    x: '0%',
                    filter: 'saturate(1)',
                    opacity: 1,
                  },
                  right: getPresenceStyles('next', isMobile),
                })}
                className="carousel-image"
                cursor={{ base: 'grab', tablet: 'unset' }}
                image={image}
                imagePair={imagePair}
                key={getImageId(image)}
                nextImage={nextImage ? nextImage.aiGeneratedImage : null}
                onClick={handleZoomActivation}
                placeholderSrc={placeholderSrc}
                position="relative"
                previousImage={
                  previousImage ? previousImage.aiGeneratedImage : null
                }
                referenceImage={referenceImage}
              />
            </AnimatePresence>
          </MotionConfig>
        </MotionCenter>
      </Center>
    );
  },
);

export { FullscreenImageCarousel };
